import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { isAuthenticated } from '../app/auth';
import { validateAuthSession } from '../app/api';
import { useTranslation } from '../app/LanguageContext';

const VALIDATE_MAX_ATTEMPTS = 3;
const VALIDATE_RETRY_DELAY_MS = 500;

const ProtectedRoute = ({ children }) => {
  const { translate } = useTranslation();
  const [isValidating, setIsValidating] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [transientError, setTransientError] = useState(false);

  const runValidation = useCallback(async () => {
    setIsValidating(true);
    setTransientError(false);
    if (!isAuthenticated()) {
      setAllowed(false);
      setIsValidating(false);
      return;
    }
    for (let attempt = 0; attempt < VALIDATE_MAX_ATTEMPTS; attempt++) {
      const result = await validateAuthSession();
      if (result.kind === 'ok') {
        setAllowed(true);
        setIsValidating(false);
        return;
      }
      if (result.kind === 'invalid') {
        setAllowed(false);
        setIsValidating(false);
        return;
      }
      if (attempt < VALIDATE_MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, VALIDATE_RETRY_DELAY_MS));
      }
    }
    setAllowed(false);
    setTransientError(true);
    setIsValidating(false);
  }, []);

  useEffect(() => {
    runValidation();
  }, [runValidation]);

  if (isValidating) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress aria-label={translate('common.loading')} />
      </Box>
    );
  }

  if (transientError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          px: 2,
        }}
      >
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => runValidation()}>
              {translate('auth.session_retry')}
            </Button>
          }
          sx={{ maxWidth: 480 }}
        >
          {translate('auth.session_validate_error')}
        </Alert>
      </Box>
    );
  }

  if (!allowed) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
